import { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import Tabs from '@mui/material/Tabs';
import Tab, { TabProps } from '@mui/material/Tab';
import { DatePicker } from 'antd';
import moment from 'moment';
import azLocale from 'antd/es/date-picker/locale/az_AZ';
import enLocale from 'antd/es/date-picker/locale/en_US';
import ruLocale from 'antd/es/date-picker/locale/ru_RU';
import 'moment/locale/az';
import 'moment/locale/en-gb';
import 'moment/locale/ru';
import { useQuery } from '@tanstack/react-query';

import useTranslation from 'customHooks/translate';
import { getAccountData } from 'services/accounts';
import { DATE_FORMAT, dateFormatList } from 'consts';
import { ACCOUNT_INFO } from 'events/types';

const AntTabs = styled(Tabs)({
  '& .MuiTabs-indicator': {
    backgroundColor: 'transparent',
  },
  '& .MuiTabs-flexContainer': {
    width: '100%',
    overflowY: 'auto',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
  },
  '& .MuiTabs-flexContainer::-webkit-scrollbar': {
    display: 'none',
  },
});

const AntTab = styled((props: TabProps) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: 'none',
    backgroundColor: '#F5F7F9',
    padding: '10px',
    fontWeight: theme.typography.fontWeightRegular,
    color: '#1D1D1D',
    borderRadius: '10px',
    minHeight: 'auto',
    '&.Mui-selected': {
      color: '#fff',
      backgroundColor: '#466AFB',
    },
  })
);

const getStartOfWeek = (date: Date) => {
  const start = new Date(date);
  start.setDate(start.getDate() - start.getDay() + 1); // Monday
  return start;
};

const getEndOfWeek = (date: Date) => {
  const end = new Date(date);
  end.setDate(end.getDate() - end.getDay() + 7); // Sunday
  return end;
};

const getStartOfMonth = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), 1);
const getEndOfMonth = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth() + 1, 0);
const getStartOfYear = (date: Date) => new Date(date.getFullYear(), 0, 1);
const getEndOfYear = (date: Date) => new Date(date.getFullYear(), 11, 31);

const formatDate = (date: Date) => {
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

// Function to parse MM.DD.YYYY string to moment object
const parseDate = (dateString: string) => {
  if (!dateString) return null;
  return moment(dateString, DATE_FORMAT);
};

// Map language code to locale
const getLocaleByLanguage = (lang: string) => {
  switch (lang) {
    case 'en':
      return enLocale;
    case 'ru':
      return ruLocale;
    case 'az':
    default:
      return azLocale;
  }
};

// Set moment locale based on language
const setMomentLocale = (lang: string) => {
  switch (lang) {
    case 'en':
      moment.locale('en-gb');
      break;
    case 'ru':
      moment.locale('ru');
      break;
    case 'az':
    default:
      moment.locale('az');
      break;
  }
};

export const ReportsTab = ({
  setDateRange,
  hasTabs = true,
  hasFromDateField = true,
}) => {
  const [value, setValue] = useState(0);
  const [selectedFromDate, setSelectedFromDate] = useState('');
  const [selectedToDate, setSelectedToDate] = useState('');
  const [language, setLanguage] = useState(
    localStorage.getItem('language') || 'az'
  );
  const [languageKey, setLanguageKey] = useState(0); // Used to force re-render
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const ignorePickerChange = useRef(false);
  const translation = useTranslation();

  // Get current locale based on language
  const currentLocale = getLocaleByLanguage(language);

  // Calculate date range based on tab value
  const calculateDateRange = (tabValue: number) => {
    const today = new Date();
    let fromDate = '';
    let toDate = '';

    switch (tabValue) {
      case 0: // today
        fromDate = toDate = formatDate(today);
        break;
      case 1: // this week
        fromDate = formatDate(getStartOfWeek(today));
        toDate = formatDate(getEndOfWeek(today));
        break;
      case 2: // last week
        const lastWeekStart = getStartOfWeek(today);
        lastWeekStart.setDate(lastWeekStart.getDate() - 7);
        fromDate = formatDate(lastWeekStart);
        const lastWeekEnd = getEndOfWeek(today);
        lastWeekEnd.setDate(lastWeekEnd.getDate() - 7);
        toDate = formatDate(lastWeekEnd);
        break;
      case 3: // this month
        fromDate = formatDate(getStartOfMonth(today));
        toDate = formatDate(getEndOfMonth(today));
        break;
      case 4: // last month
        const lastMonth = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1
        );
        fromDate = formatDate(getStartOfMonth(lastMonth));
        toDate = formatDate(getEndOfMonth(lastMonth));
        break;
      case 5: // this year
        fromDate = formatDate(getStartOfYear(today));
        toDate = formatDate(getEndOfYear(today));
        break;
      case 6: // last year
        const lastYear = new Date(today.getFullYear() - 1, 0, 1);
        fromDate = formatDate(getStartOfYear(lastYear));
        toDate = formatDate(getEndOfYear(lastYear));
        break;
      default:
        break;
    }

    return { fromDate, toDate };
  };

  // Check for language changes in localStorage
  useEffect(() => {
    // Set initial locale
    setMomentLocale(language);

    // Check for language changes every second
    intervalRef.current = setInterval(() => {
      const currentLanguage = localStorage.getItem('language') || 'az';
      if (currentLanguage !== language) {
        setLanguage(currentLanguage);
        setMomentLocale(currentLanguage);
        setLanguageKey((prev) => prev + 1); // Increment key to force re-render
      }
    }, 1000); // Check every second

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [language]);

  const handleChange = (event, newValue) => {
    // Get new dates based on tab selection
    const { fromDate, toDate } = calculateDateRange(newValue);

    // IMPORTANT: Always update DatePicker values regardless of tab
    // This ensures the parent component sees the correct values if it's
    // directly accessing the DOM or refs
    ignorePickerChange.current = true;
    setSelectedFromDate(fromDate);
    setSelectedToDate(toDate);

    // Update tab value
    setValue(newValue);

    // Update parent component with correct date range
    setDateRange(fromDate, toDate);

    // Reset flag after state updates are processed
    setTimeout(() => {
      ignorePickerChange.current = false;
    }, 0);
  };

  const handleFromDateChange = (date, dateString) => {
    if (ignorePickerChange.current) return;

    setSelectedFromDate(dateString);

    // Only manually selected dates should affect parent component if we're on Today tab
    if (value === 0) {
      setDateRange(dateString, selectedToDate || dateString);
    }
  };

  const handleToDateChange = (date, dateString) => {
    if (ignorePickerChange.current) return;

    setSelectedToDate(dateString);

    // Only manually selected dates should affect parent component if we're on Today tab
    if (value === 0) {
      setDateRange(selectedFromDate || dateString, dateString);
    }
  };

  const accountInfoQuery = useQuery({
    queryKey: [ACCOUNT_INFO],
    queryFn: getAccountData,
    select: (response) => response.data.data,
  });

  useEffect(() => {
    if (accountInfoQuery.data?.timeZoneId) {
      const updatedDate = moment()
        .tz(accountInfoQuery.data.timeZoneId)
        .format(DATE_FORMAT);
      setSelectedFromDate(updatedDate);
      setSelectedToDate(updatedDate);
    }
  }, [accountInfoQuery.data?.timeZoneId]);

  useEffect(() => {
    setDateRange(selectedFromDate, selectedToDate);
  }, [selectedFromDate, selectedToDate]);

  const isTodaySelected = value === 0;

  return (
    <div className="flex flex-col gap-[20px]">
      {hasTabs && (
        <AntTabs
          value={value}
          onChange={handleChange}
          className="ant-tabs no-scrollbar"
          aria-label="ant example"
        >
          <AntTab label={translation.reports.Today} />
          <AntTab label={translation.reports.ThisWeek} />
          <AntTab label={translation.reports.LastWeek} />
          <AntTab label={translation.reports.ThisMonth} />
          <AntTab label={translation.reports.LastMonth} />
          <AntTab label={translation.reports.ThisYear} />
          <AntTab label={translation.reports.LastYear} />
        </AntTabs>
      )}

      {isTodaySelected && (
        <div className="grid w-full grid-cols-1 gap-[12px] md:grid-cols-2">
          {hasFromDateField && (
            <div className="flex flex-col gap-[12px]">
              <p className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
                {translation.reports.StartDate}
              </p>
              <DatePicker
                dropdownClassName="custom-datepicker-popup"
                getPopupContainer={(triggerNode) =>
                  (triggerNode.parentElement as HTMLElement) ?? document.body
                }
                id="report-from-date-picker" // Add consistent ID for easier debugging
                key={`from-date-${languageKey}`} // Key to force re-render on language change
                placeholder={translation.reports.SelectDate}
                format={dateFormatList}
                className="border-1 h-[50px] rounded-[6px]"
                onChange={handleFromDateChange}
                value={selectedFromDate ? parseDate(selectedFromDate) : null}
                locale={currentLocale}
              />
            </div>
          )}
          <div className="flex flex-col gap-[12px]">
            <p className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
              {translation.reports.EndDate}
            </p>
            <DatePicker
              dropdownClassName="custom-datepicker-popup"
              getPopupContainer={(triggerNode) =>
                (triggerNode.parentElement as HTMLElement) ?? document.body
              }
              className="border-1 h-[50px] rounded-[6px]"
              id="report-to-date-picker" // Add consistent ID for easier debugging
              key={`to-date-${languageKey}`} // Key to force re-render on language change
              placeholder={translation.reports.SelectDate}
              format={dateFormatList}
              onChange={handleToDateChange}
              value={selectedToDate ? parseDate(selectedToDate) : null}
              locale={currentLocale}
            />
          </div>
        </div>
      )}
    </div>
  );
};
